import React, { useState } from "react";
import { Loader2, AlertTriangle, RefreshCw, Inbox } from "lucide-react";
import { useOpportunity } from "./useOpportunity";
import { OpportunityHead } from "./OpportunityHead";
import { OpportunityAnswer } from "./OpportunityAnswer";
import { OpportunityBuild } from "./OpportunityBuild";
import { OpportunityWhere } from "./OpportunityWhere";
import { OpportunityChange } from "./OpportunityChange";
import { FollowUps } from "../search/FollowUps";
import { OPP_MODEL, streamOppFollowUp } from "../../lib/opportunityApi";

const SUGGEST = ["Which three actions should we approve first?", "How much of the total is counted more than once?",
  "Which branch needs attention most?", "What does the aging amount really mean?"];

function Note({ icon: Icon, testid, tone = "text-muted", children }) {
  return (
    <div data-testid={testid} className={`flex items-start gap-2.5 text-[13px] ${tone} border border-border bg-surface rounded-sm px-4 py-3.5`}>
      <Icon size={15} className="shrink-0 mt-0.5" /><div>{children}</div>
    </div>
  );
}

function Footer({ s, onFresh }) {
  if (s.status !== "done" || !s.done?.id) return null;
  const when = s.done.created_at ? new Date(s.done.created_at).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" }) : "";
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted border-t border-border pt-3">
      <span data-testid="opp-answer-meta">{s.model || OPP_MODEL} · {s.done.cached ? `saved answer from ${when}` : `answered ${when}`}</span>
      <button type="button" data-testid="opp-refresh" onClick={onFresh}
              className="inline-flex items-center gap-1.5 px-2 py-1 border border-border rounded-sm hover:border-primary hover:text-primary transition-colors">
        <RefreshCw size={11} /> Fresh answer
      </button>
    </div>
  );
}

export default function OpportunityBody({ req, onFresh, origin }) {
  const s = useOpportunity(req);
  const [seed, setSeed] = useState(null);
  const f = s.facts;
  const ready = s.status === "done" && s.done?.id;
  const ask = (q) => {
    setSeed({ q, n: Date.now() });
    document.querySelector('[data-testid="opp-followups"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  if (s.status === "empty") return <Note icon={Inbox} testid="opp-empty">The latest daily cycle has no actions to explain. Select “Run cycle now” on the Morning Brief to score a fresh day.</Note>;
  return (
    <div className="space-y-7" data-testid="opp-result">
      {!f && s.status !== "error" && <Note icon={Loader2} testid="opp-loading">Reading the latest daily cycle…</Note>}
      {f && <OpportunityHead f={f} origin={origin} />}
      {f && <OpportunityAnswer text={s.text} streaming={s.status === "streaming" || s.status === "loading"} />}
      {s.status === "error" && <Note icon={AlertTriangle} testid="opp-error" tone="text-critical">{s.error}</Note>}
      <Footer s={s} onFresh={onFresh} />
      {ready && (
        <FollowUps key={s.done.id} id={s.done.id} initial={s.done.turns} stream={streamOppFollowUp} prefix="opp-followup" seed={seed}
                   title="Ask about the opportunity" suggestions={SUGGEST}
                   placeholder="Ask a follow-up, e.g. “why is Pasig Ortigas so high?” or “what about the DKS510?”" />
      )}
      {f && <OpportunityBuild f={f} />}
      {f && <OpportunityWhere f={f} onAsk={ready ? ask : null} />}
      {f && <OpportunityChange change={f.change} />}
    </div>
  );
}
